import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";

interface ConfirmModalProps {
    title: string;
    message: string;
    onClose: () => void;
    onConfirm: () => void;
    type: "normal" | "delete";
}

function ConfirmModal({
    title,
    message,
    onClose,
    onConfirm,
    type,
}: ConfirmModalProps) {
    const overlayRef = useRef<HTMLDivElement | null>(null);

    const dismissModal = useCallback(() => {
        onClose();
    }, [onClose]);

    const handleConfirm = () => {
        onConfirm();
        dismissModal();
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node)
            ) {
                dismissModal();
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [dismissModal]);

    return createPortal(
        <div className="fixed inset-0 left-0 top-0 z-[1000] flex h-dvh min-h-dvh w-full place-items-center items-center justify-center bg-black bg-opacity-50 p-4">
            <div
                className="z-[500] flex max-h-full w-full flex-col gap-1 overflow-y-auto rounded-2xl border-2 border-border-color bg-background-color p-6 text-text-color sm:w-[400px]"
                ref={overlayRef}
            >
                <h2 className="text-xl font-semibold">{title}</h2>
                <p className="text-base">{message}</p>
                <div className="mt-4 flex justify-end gap-4 text-base">
                    <button
                        className="btn btn-pill-reset"
                        onClick={dismissModal}
                        autoFocus
                    >
                        Cancel
                    </button>
                    {type === "delete" ? (
                        <button
                            className="btn btn-pill-danger"
                            onClick={handleConfirm}
                        >
                            Delete
                        </button>
                    ) : (
                        <button
                            className="btn btn-pill-primary"
                            onClick={handleConfirm}
                        >
                            Confirm
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body, // Render the modal at the end of the body
    );
}

export default ConfirmModal;
